import React, { useEffect, useState } from "react";
import { humanize } from '../../utils/adminFormat';
import useClientPaging from '../../components/admin/useClientPaging';
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import api from "../../api";
import SendWhatsAppModal from "../../components/SendWhatsAppModal";

/**
 * Bulk WhatsApp from leads. Route requires send-whatsapp-msg; GET /admin/leads allows that permission too.
 */
const WhatsAppCampaign = () => {
  const [leads, setLeads] = useState([]);
  const { pageRows: pagedLeads, pager: leadsPager } = useClientPaging(leads);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    course: "",
    source: "",
    status: "",
  });

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async (customFilters = null) => {
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      const activeFilters = customFilters || filters;
      Object.keys(activeFilters).forEach((key) => {
        if (activeFilters[key]) {
          params.append(key, activeFilters[key]);
        }
      });
      const res = await api.get(`/admin/leads?${params.toString()}`);
      setLeads(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching leads", err);
      setError(
        err.response?.data?.message ||
          "Failed to fetch leads. Please check your connection or permissions."
      );
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSelect = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.filter((lead) => lead.phone).map((lead) => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "info";
      case "contacted":
        return "warning";
      case "replied":
        return "primary";
      case "converted":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={1} fontWeight={700}>
        WhatsApp Campaign
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Select leads and send a WhatsApp message or attachment in bulk.
      </Typography>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#ffebee", color: "#c62828" }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              size="small"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              size="small"
              value={filters.email}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Course"
              name="course"
              size="small"
              value={filters.course}
              onChange={handleFilterChange}
              placeholder="Name or ID"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                name="source"
                label="Source"
                value={filters.source}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="website_contact_form">Website Form</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                label="Status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="replied">Replied</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => fetchLeads()}>
                Filter
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const reset = {
                    name: "",
                    email: "",
                    course: "",
                    source: "",
                    status: "",
                  };
                  setFilters(reset);
                  fetchLeads(reset);
                }}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Stack
        direction="row"
        spacing={2}
        mb={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          variant="contained"
          color="success"
          disabled={!selectedLeads.length}
          onClick={() => setOpenModal(true)}
        >
          Send WhatsApp ({selectedLeads.length})
        </Button>
        <Typography variant="body2" color="text.secondary">
          Total Leads: {leads.length}
        </Typography>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      leads.some((lead) => lead.phone) &&
                      selectedLeads.length === leads.filter((lead) => lead.phone).length
                    }
                    indeterminate={
                      selectedLeads.length > 0 &&
                      selectedLeads.length < leads.filter((lead) => lead.phone).length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length > 0 ? (
                pagedLeads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelect(lead.id)}
                        disabled={!lead.phone}
                        title={lead.phone ? undefined : "No phone number"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{lead.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.phone || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lead.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.course ? lead.course.name : "-"}
                    </TableCell>
                    <TableCell>{humanize(lead.source)}</TableCell>
                    <TableCell>
                      <Chip
                        label={humanize(lead.status)}
                        color={getStatusColor(lead.status)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No leads found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {leadsPager}
        </>
      )}

      <SendWhatsAppModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        selectedLeads={selectedLeads}
        onSent={() => setSelectedLeads([])}
        refreshLeads={() => fetchLeads()}
      />
    </Box>
  );
};

export default WhatsAppCampaign;
